import { useCallback, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Check, CircleX, LoaderCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import type { Violation, ViolationSeverity } from '@conciair/scheduler-core';
import { Button, Chip } from '../../ui/components';
import { Modal } from '../../ui/dialog';
import { generateDraft, type GenerateOutcome, type GenerateStep } from './generateDraft';
import { qk } from '../../data/queries';
import { resolveWhere } from '../../engine/snapshot';

const STEPS: { id: GenerateStep; label: string }[] = [
  { id: 'snapshot', label: 'Read inputs' },
  { id: 'validate', label: 'Validate' },
  { id: 'generate', label: 'Coverage check and assignment' },
  { id: 'store', label: 'Store the draft' },
];

const ORDER: GenerateStep[] = ['snapshot', 'validate', 'generate', 'store', 'done'];

export function useGenerateDraft(orgId: string) {
  const qc = useQueryClient();
  const [step, setStep] = useState<GenerateStep | null>(null);
  const mutation = useMutation({
    mutationFn: () => generateDraft(orgId, setStep),
    onSuccess: async (outcome) => {
      if (outcome.kind === 'ok') {
        await Promise.all([
          qc.invalidateQueries({ queryKey: qk.org(orgId) }),
          qc.invalidateQueries({ queryKey: ['schedule_run'] }),
          qc.invalidateQueries({ queryKey: ['schedule_assignments'] }),
        ]);
      }
    },
  });
  const reset = useCallback(() => {
    setStep(null);
    mutation.reset();
  }, [mutation]);
  return { ...mutation, step, reset };
}

export function GenerateDialog({
  open,
  onOpenChange,
  step,
  outcome,
  error,
  pending,
}: {
  open: boolean;
  onOpenChange(open: boolean): void;
  step: GenerateStep | null;
  outcome: GenerateOutcome | undefined;
  error: Error | null;
  pending: boolean;
}) {
  const stepIndex = step ? ORDER.indexOf(step) : -1;
  return (
    <Modal
      open={open}
      onOpenChange={(o) => !pending && onOpenChange(o)}
      title="Generate Draft"
      description="Validates, assigns crews, stores the draft. Nothing is published."
      footer={
        <>
          {outcome?.kind === 'ok' && (
            <Link
              to="/scheduler/schedule"
              onClick={() => onOpenChange(false)}
              className="inline-flex items-center rounded-md bg-navy px-3 py-1.5 text-sm font-medium text-white hover:bg-navy-deep"
            >
              Open Schedule
            </Link>
          )}
          <Button disabled={pending} onClick={() => onOpenChange(false)}>Close</Button>
        </>
      }
    >
      <ol className="mb-4 space-y-1.5">
        {STEPS.map((s, i) => {
          const state = error && i === stepIndex ? 'error' : i < stepIndex ? 'done' : i === stepIndex && pending ? 'active' : 'todo';
          return (
            <li key={s.id} className="flex items-center gap-2 text-sm">
              {state === 'done' && <Check size={16} className="text-ok" />}
              {state === 'active' && <LoaderCircle size={16} className="animate-spin text-navy" />}
              {state === 'error' && <CircleX size={16} className="text-danger" />}
              {state === 'todo' && <span className="inline-block h-4 w-4 rounded-full border border-line" />}
              <span className={state === 'todo' ? 'text-ink-muted' : 'text-ink'}>{s.label}</span>
            </li>
          );
        })}
      </ol>

      {error && (
        <div role="alert" className="rounded-md border border-danger-soft bg-danger-soft px-3 py-2 text-sm text-danger">
          {error.message}
        </div>
      )}

      {outcome && <Outcome outcome={outcome} />}
    </Modal>
  );
}

function Outcome({ outcome }: { outcome: GenerateOutcome }) {
  if (outcome.kind === 'invalid') {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-danger">
          {outcome.errors.length} input problem{outcome.errors.length === 1 ? '' : 's'} block generation. Nothing was written.
        </p>
        <MessageList messages={outcome.errors} tone="danger" input={outcome.input} />
        {outcome.warnings.length > 0 && <MessageList messages={outcome.warnings} tone="warn" input={outcome.input} />}
      </div>
    );
  }
  if (outcome.kind === 'engine-errors') {
    return (
      <div className="space-y-3">
        <p className="text-sm font-medium text-danger">The engine could not honor every override. Nothing was written.</p>
        <ul className="space-y-1 text-sm">
          {outcome.errors.map((e, i) => (
            <li key={i} className="rounded border border-danger-soft bg-danger-soft/60 px-2 py-1 text-danger">
              {e}
            </li>
          ))}
        </ul>
      </div>
    );
  }
  const r = outcome.result;
  const gaps = r.schedule.reduce((n, row) => n + (row.pic.pilotId || row.pic.absorbed ? 0 : 1) + (row.sic.pilotId || row.sic.absorbed ? 0 : 1), 0);
  const bySeverity = countBySeverity(r.violations);
  // Rotations already in JetInsight are pinned by the snapshot (D97); say so, since they could not move.
  const held = new Set(outcome.input.overrides.filter((o) => o.source === 'posted' || o.notes === 'Posted to JetInsight').map((o) => o.tail + '|' + o.rotationStart)).size;
  return (
    <div className="space-y-3 text-sm">
      <p className="font-medium text-ok">Draft stored. It replaces the previous draft.</p>
      <dl className="grid grid-cols-2 gap-x-6 gap-y-1">
        <dt className="text-ink-muted">Rotations</dt>
        <dd>{r.schedule.length}</dd>
        {held > 0 && (
          <>
            <dt className="text-ink-muted">Held for JetInsight</dt>
            <dd>{held} already posted, unchanged</dd>
          </>
        )}
        <dt className="text-ink-muted">Unfilled seats</dt>
        <dd className={gaps ? 'font-semibold text-danger' : ''}>{gaps}</dd>
        <dt className="text-ink-muted">Findings</dt>
        <dd className="flex flex-wrap gap-1">
          {(['HIGH', 'WARN', 'SOFT', 'INFO'] as ViolationSeverity[]).map((s) =>
            bySeverity[s] ? (
              <Chip key={s} tone={s === 'HIGH' ? 'danger' : s === 'WARN' ? 'warn' : s === 'SOFT' ? 'gold' : 'neutral'}>
                {bySeverity[s]} {s}
              </Chip>
            ) : null
          )}
          {r.violations.length === 0 && <span>none</span>}
        </dd>
        <dt className="text-ink-muted">Coverage forecast</dt>
        <dd>{r.coverageWarnings.length ? `${r.coverageWarnings.length} warning${r.coverageWarnings.length === 1 ? '' : 's'}` : 'OK'}</dd>
        <dt className="text-ink-muted">Input warnings</dt>
        <dd>{r.warnings.length || 'none'}</dd>
        <dt className="text-ink-muted">Engine</dt>
        <dd className="font-mono text-xs">{outcome.engineVersion} in {Math.round(outcome.ms)} ms</dd>
      </dl>
      {r.warnings.length > 0 && (
        <ul className="space-y-1">
          {r.warnings.map((w, i) => (
            <li key={i} className="rounded border border-warn-soft bg-warn-soft/60 px-2 py-1 text-warn">
              {w}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function MessageList({ messages, tone, input }: { messages: { where: string; message: string }[]; tone: 'danger' | 'warn'; input: GenerateOutcome['input'] }) {
  const cls = tone === 'danger' ? 'border-danger-soft bg-danger-soft/60 text-danger' : 'border-warn-soft bg-warn-soft/60 text-warn';
  return (
    <ul className="space-y-1 text-sm">
      {messages.map((m, i) => {
        const where = resolveWhere(m.where, input);
        return (
          <li key={i} className={`rounded border px-2 py-1 ${cls}`}>
            <span className="font-medium">{where.area}{where.row ? ` (record ${where.row})` : ''}:</span> {m.message}
          </li>
        );
      })}
    </ul>
  );
}

export function countBySeverity(violations: Violation[]): Record<ViolationSeverity, number> {
  const out: Record<ViolationSeverity, number> = { HIGH: 0, WARN: 0, SOFT: 0, INFO: 0 };
  for (const v of violations) out[v.severity] = (out[v.severity] ?? 0) + 1;
  return out;
}
